import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Plus, Edit, Trash2, Eye, EyeOff, GripVertical, Save, X } from 'lucide-react';

const ServicesManager = () => {
  const api = useApi();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state for creating/editing services
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    features: [''],
    icon: 'Code',
    order: 0,
    is_active: true
  });

  const iconOptions = [
    'Code', 'Brain', 'Shield', 'Globe', 'Smartphone', 'Database',
    'Lock', 'Zap', 'Settings', 'Users', 'Cloud', 'Monitor'
  ];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/services');
      if (response.success) {
        setServices(response.data);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async () => {
    try {
      const serviceData = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== ''),
        order: services.length + 1
      };

      const response = await api.post('/admin/services', serviceData);
      if (response.success) {
        loadServices();
        resetForm();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating service:', error);
    }
  };

  const handleUpdateService = async () => {
    try {
      const serviceData = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== '')
      };

      const response = await api.put(`/admin/services/${editingService}`, serviceData);
      if (response.success) {
        loadServices();
        resetForm();
        setEditingService(null);
      }
    } catch (error) {
      console.error('Error updating service:', error);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const response = await api.delete(`/admin/services/${serviceId}`);
        if (response.success) {
          loadServices();
        }
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const handleToggleActive = async (serviceId) => {
    try {
      const response = await api.put(`/admin/services/${serviceId}/toggle`);
      if (response.success) {
        loadServices();
      }
    } catch (error) {
      console.error('Error toggling service status:', error);
    }
  };

  const startEdit = (service) => {
    setFormData({
      title: service.title,
      description: service.description,
      features: service.features.length > 0 ? service.features : [''],
      icon: service.icon,
      order: service.order,
      is_active: service.is_active
    });
    setEditingService(service.id);
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      features: [''],
      icon: 'Code',
      order: 0,
      is_active: true
    });
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const updateFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index)
      }));
    }
  };

  const ServiceCard = ({ service }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <span className="text-blue-600 font-semibold">{service.icon}</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
            <p className="text-sm text-gray-500">Order: {service.order}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleActive(service.id)}
            className={`p-2 rounded-lg ${service.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
            title={service.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
          >
            {service.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
          <button
            onClick={() => startEdit(service)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="Edit service"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteService(service.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete service"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
      
      {service.features && service.features.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Features</p>
          <div className="flex flex-wrap gap-1">
            {service.features.map((feature, index) => (
              <span
                key={index}
                className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const ServiceForm = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingService ? 'Edit Service' : 'Create New Service'}
        </h3>
        <button
          onClick={() => {
            setEditingService(null);
            setShowCreateForm(false);
            resetForm();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
        >
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Service title"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Service description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
          <select
            value={formData.icon}
            onChange={(e) => setFormData(prev => ({...prev, icon: e.target.value}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Features</label>
            <button
              type="button"
              onClick={addFeature}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              + Add Feature
            </button>
          </div>
          <div className="space-y-2">
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Feature description"
                />
                {formData.features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Active (visible to public)
          </label>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={editingService ? handleUpdateService : handleCreateService}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            <span>{editingService ? 'Update Service' : 'Create Service'}</span>
          </button>
          <button
            onClick={() => {
              setEditingService(null);
              setShowCreateForm(false);
              resetForm();
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Services Manager</h2>
          <p className="text-gray-600 mt-1">Manage your company services and their visibility</p>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingService(null);
            resetForm();
          }}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span>Add Service</span>
        </button>
      </div>

      {(showCreateForm || editingService) && <ServiceForm />}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {services.map(service => (
          <ServiceCard key={service.id} service={service} />
        ))}
      </div>

      {services.length === 0 && !showCreateForm && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No services found</p>
          <button
            onClick={() => {
              setShowCreateForm(true);
              resetForm();
            }}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Create your first service
          </button>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;